<template>
    <div class="cal-entry-dialog">
        <form novalidate class="md-layout" @keyup="handleEnterButton">
            <md-card  style="min-width:560px;">
                <md-card-header>
                    <b-row>
                        <b-col><div class="md-title">New manual event</div></b-col>
                        <b-col style="text-align:right">
                            <md-button class="md-icon-button md-dense" @click="$emit('closed')"><md-icon>highlight_off</md-icon></md-button>
                        </b-col>
                    </b-row>
                </md-card-header>
                <md-card-content>
                   <b-row v-if="propertyTitle">
                       <b-col>
                           <p>{{ propertyTitle }}</p>
                       </b-col>
                   </b-row>
                    <b-row>
                        <b-col>
                            <md-field>
                                <label>Tenant name</label>
                                <md-input placeholder="Name" v-model="tenantName" required></md-input>
                            </md-field>
                        </b-col>
                        <b-col>
                            <md-field>
                                <label>Tenant surname</label>
                                <md-input placeholder="Surname" v-model="tenantSurname" required></md-input>
                            </md-field>
                        </b-col>
                    </b-row>
                   <b-row>
                       <b-col>
                           <md-datepicker
                               v-model="startDate"
                               :class="messageClass"
                               :md-disabled-dates="disabledStartDates"
                               md-immediately
                               required
                           >
                               <label>Start date</label>
                               <span class="md-error">Selected period conflicts with other entries</span>
                           </md-datepicker>
                       </b-col>
                       <b-col>
                           <md-datepicker
                                   v-if="!termless"
                                   v-model="endDate"
                                   :class="messageClass"
                                   :disabled="!termless"
                                   :md-disabled-dates="disabledEndDates"
                                   md-immediately
                           >
                               <label>End date</label>
                               <span class="md-error">Selected period conflicts with other entries</span>
                           </md-datepicker>
                           <md-field v-else>
                               <label>Disabled</label>
                               <md-input placeholder="Entry is termless" disabled></md-input>
                           </md-field>
                       </b-col>
                   </b-row>
                    <b-row>
                        <b-col>
                            <md-field>
                                <label for="bookingStatus">Entry status</label>
                                <md-select v-model="bookingStatus" id="bookingStatus" required>
                                    <md-option
                                            v-for="(status, index) in bookingStatuses"
                                            :key="index"
                                            :value="status.value"
                                    >{{ status.title }}</md-option>
                                </md-select>
                            </md-field>
                        </b-col>
                        <b-col>
                            <md-checkbox v-model="termless" class="md-primary">Termless entry</md-checkbox>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <md-field>
                                <label>Entry Description</label>
                                <md-textarea v-model="statusComment"></md-textarea>
                            </md-field>
                            * This will create calendar event but not a new deal!
                        </b-col>
                    </b-row>
                </md-card-content>

                <md-card-actions v-if="!readonly">
                    <md-button class="md-button" @click="$emit('closed')">Cancel</md-button>
                    <md-progress-spinner v-if="loading"  :md-diameter="30" :md-stroke="3" md-mode="indeterminate" style="color:white"></md-progress-spinner>
                    <md-button
                            :disabled="!isFormValid || loading"
                            class="md-primary"
                            @click="onCheckAvailability"
                    >Create</md-button>
                </md-card-actions>
            </md-card>
        </form>
        <event-overlapping-confirmation-dialog
                v-model="eventOverlappingConfirmationDialogOpened"
                @confirmed="onCreateEntry"
        ></event-overlapping-confirmation-dialog>
    </div>
</template>

<script>
    import { mapState, mapActions } from 'vuex';
    import EventOverlappingConfirmationDialog from '@/components/property-calendar/dialogs/EventOverlappingConfirmationDialog.vue';

    export default {
        name: "CreateEntryDialog",
        components: { EventOverlappingConfirmationDialog },
        props: {
            propertyId: {
                type: String,
                required: true,
            },
            selectedStartDate: {
                type: Date,
                default: () => ( new Date() ),
            },
            readonly:{
                type: Boolean,
                required: false,
                default: false
            }
        },
        created() {
            this.startDate = this.selectedStartDate;
        },
        data() {
            return {
                startDate: null,
                endDate: null,
                bookingStatuses: [
                    {
                        title: 'Pre-booked',
                        value: 'prebooked',
                    },
                    {
                        title: 'Booked',
                        value: 'booked',
                    },
                    {
                        title: 'Blocked',
                        value: 'blocked',
                    },
                ],
                bookingStatus: '',
                statusComment: '',
                loading: false,
                selectedDatesAvailable: true,
                termless: false,
                tenantName: '',
                tenantSurname: '',
                eventOverlappingConfirmationDialogOpened: false,
            };
        },
        computed: {
            ...mapState({ activeProperties: state => state.propertiesModule.activeProperties }),
            property() {
                return this.activeProperties.find((item) => item._id === this.propertyId);
            },
            propertyTitle() {
                if (!this.property || !this.property._id) {
                    return null;
                }
                try {
                    const {
                        marketingDet: { shortDesc: title } = {},
                        location: { address: { street, number } = {} } = {},
                    } = this.property;

                    return `${title}, ${street} ${number}`;
                }
                catch (error) {
                    console.log(error);
                    return '';
                }
            },
            isFormValid() {
                return this.datesValid && this.bookingStatus && this.tenantPresent;
            },
            tenantPresent() {
                if (this.bookingStatus === 'blocked') {
                    return true;
                }

                return this.tenantName && this.tenantSurname;
            },
            datesValid() {
                return this.startDate && (this.endDate || this.termless);
            },
            messageClass() {
                return {
                    'md-invalid': !this.selectedDatesAvailable,
                }
            }
        },
        methods: {
            ...mapActions('propertyCalendarModule', ['createEntry', 'checkAvailability']),
            disabledStartDates(date) {
                if (!this.endDate) {
                    return false;
                }

                return date.getTime() > this.endDate.getTime();
            },
            disabledEndDates(date) {
                if (!this.startDate) {
                    return false;
                }

                return date.getTime() < this.startDate.getTime();
            },
            resetData() {
                this.startDate = null;
                this.endDate = null;
                this.bookingStatus = '';
                this.statusComment = '';
                this.termless = false;
                this.selectedDatesAvailable = true;
                this.tenantName = '';
                this.tenantSurname = '';
                this.eventOverlappingConfirmationDialogOpened = false;
            },
            notifyUser(title, msg) {
                this.$notify({
                    title: title,
                    message: msg
                });
            },
            async onCheckAvailability() {
                try {
                    this.loading = true;
                    const available = await this.checkAvailability({
                        propertyId: this.propertyId,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        termless: this.termless,
                    });
                    if (!available) {
                        this.selectedDatesAvailable = false;
                        this.eventOverlappingConfirmationDialogOpened = true;
                        return;
                    }
                    this.selectedDatesAvailable = true;
                    await this.onCreateEntry();
                }
                catch (error) {
                    this.notifyUser('Server error', 'Something is not right');
                }
                finally {
                    this.loading = false;
                }
            },
            async onCreateEntry() {
               try {
                   this.loading = true;
                   const payload = {
                       propertyId: this.propertyId,
                       startDate: this.startDate,
                       endDate: this.endDate,
                       termless: this.termless,
                       bookingStatus: this.bookingStatus,
                       statusComment: this.statusComment,
                       tenantName: this.tenantName,
                       tenantSurname: this.tenantSurname,
                   };

                   await this.createEntry(payload);
                   this.$emit('closed');
                   this.resetData();
               }
               catch (error) {
                   this.notifyUser('Server error', 'Something is not right');
               }
               finally {
                   this.loading = false;
               }
            },
            handleEnterButton(event) {
                if (event.keyCode !== 13) {
                    return;
                }
                if (!this.isFormValid) {
                    return;
                }
                this.onCheckAvailability();
            },
        },
        watch: {
            selectedStartDate(value) {
                this.startDate = value;
            },
            startDate() {
                this.selectedDatesAvailable = true;
            },
            endDate() {
                this.selectedDatesAvailable = true;
            },
        }
    }
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/property-calendar/dialogs/CreateEntryDialog.vue